/**
 * Initialize carousel DOM restructuring
 *
 * Moves .splide__pagination above .splide__arrow--next within carousel
 * containers to control visual ordering without affecting Splide functionality.
 * Runs on initial load, window resize, and Splide lifecycle events.
 *
 * @since 1.0.0
 */

const carouselSelector = '.carousel-split, .carousel-dynamic';

/**
 * Reorder pagination relative to the next arrow inside .splide__arrows.
 *
 * @param {Element} carousel
 */
export function reorderCarouselPagination(carousel) {
    const pagination = carousel.querySelector('.splide__pagination');
    const arrowNext = carousel.querySelector('.splide__arrow--next');

    if (pagination && arrowNext) {
        arrowNext.parentNode.insertBefore(pagination, arrowNext);
    }
}

/**
 * Hide the arrows container when the carousel doesn't overflow and no pagination exists.
 *
 * Splide doesn't always properly disable arrows when there are fewer items than
 * can fit in the viewport, so we check for actual overflow by comparing the list
 * size to the track size.
 *
 * @param {Element} carousel
 */
export function hideCarouselArrowsIfDisabled(carousel) {
    const arrowsContainer = carousel.querySelector('.splide__arrows');
    const arrows = carousel.querySelectorAll('.splide__arrow');
    const track = carousel.querySelector('.splide__track');
    const list = carousel.querySelector('.splide__list');

    if (arrowsContainer && arrows.length > 0 && track && list) {
        const allDisabled = Array.from(arrows).every((arrow) => arrow.disabled);
        const hasOverflow = list.scrollWidth > track.clientWidth;

        /*
         * Hide arrows (button elements) when all are disabled or content doesn't overflow.
         * The .splide__arrows container stays visible for pagination dots inside it.
         */
        if (allDisabled || !hasOverflow) {
            carousel.classList.add('has-no-arrows');
        } else {
            carousel.classList.remove('has-no-arrows');
        }
    }
}

/**
 * Make all pagination buttons tabbable by removing tabindex="-1" from inactive buttons.
 *
 * Splide sets inactive pagination buttons to tabindex="-1" to remove them from tab order,
 * but for accessibility, all pagination buttons should be tabbable.
 *
 * @param {Element} carousel
 */
export function makeAllPaginationButtonsTabbable(carousel) {
    const paginationButtons = carousel.querySelectorAll('.splide__pagination__page');

    if (paginationButtons.length > 0) {
        paginationButtons.forEach((button) => {
            button.removeAttribute('tabindex');
        });
    }
}

/**
 * Reorder pagination and sync arrow visibility for a single carousel.
 *
 * @param {Element} carousel
 */
export function syncCarouselControls(carousel) {
    reorderCarouselPagination(carousel);
    hideCarouselArrowsIfDisabled(carousel);
    makeAllPaginationButtonsTabbable(carousel);

    if (carousel.closest('.carousel-cards-icon') && carousel.splide) {
        carousel.dispatchEvent(new CustomEvent('carousel-cards-icon:sync'));
    }
}

/**
 * @param {Event} event
 */
function handleSplideCarouselEvent(event) {
    const carousel = event.target.closest(carouselSelector);

    if (carousel) {
        syncCarouselControls(carousel);
    }
}

/**
 * Reorder pagination and sync controls for all carousels.
 */
function reorderAllCarousels() {
    document.querySelectorAll(carouselSelector).forEach(syncCarouselControls);
}

export function initCarousel() {
    reorderAllCarousels();

    let resizeTimeout;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(reorderAllCarousels, 150);
    });

    document.addEventListener('splide:mounted', handleSplideCarouselEvent);
    document.addEventListener('splide:updated', handleSplideCarouselEvent);
    document.addEventListener('splide:resized', handleSplideCarouselEvent);
    document.addEventListener('splide:refresh', handleSplideCarouselEvent);
}
